import { useMemo, useState } from 'react'
import { AlertTriangle, ArrowDownUp, Check, ChevronDown, Copy, ExternalLink, RefreshCw, Star, WifiOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import {
  MissingRateError,
  convert,
  crossRate,
  getFreshness,
  maxAgeFor,
  parseAmount,
  rateFractionDigits,
  type ConversionResult,
} from '@globalx/core'
import { CurrencyFlag } from '@/components/currency/CurrencyFlag'
import { CurrencyPicker } from '@/components/currency/CurrencyPicker'
import { Button, KindBadge, Skeleton } from '@/components/ui/primitives'
import { useFormat } from '@/lib/format'
import { isSavedPair, useConverter } from '@/store/converter'
import { AmountInput } from './AmountInput'
import { useOnline, useRates, type LiveRates } from './useRates'

const COMMON_AMOUNTS = ['1', '10', '100', '1000', '10000']

function CurrencyButton({ code, onClick, label }: { code: string; onClick: () => void; label: string }) {
  const { currencyName } = useFormat()
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${label}: ${code} — ${currencyName(code)}`}
      className="group flex min-w-0 items-center gap-2.5 rounded-xl py-1 pe-2 ps-1 text-start transition hover:bg-surface-3/70"
    >
      <CurrencyFlag code={code} className="size-10" />
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="flex items-center gap-1 text-[1.05rem] font-semibold">
          <span className="ltr">{code}</span>
          <ChevronDown className="size-4 text-subtle transition group-hover:text-fg" aria-hidden />
        </span>
        <span className="max-w-[6rem] truncate text-xs text-muted xs:max-w-[8.5rem] sm:max-w-[11rem]">{currencyName(code)}</span>
      </span>
    </button>
  )
}

function useConversion(snapshot: LiveRates | null) {
  const { from, to, amount } = useConverter()
  return useMemo(() => {
    const parsed = parseAmount(amount)
    if (!snapshot) return { rate: null, inverse: null, result: null, parsed, missing: null }
    try {
      const rate = crossRate(snapshot, from, to)
      const result: ConversionResult | null = parsed ? convert(snapshot, parsed, from, to) : null
      return { rate, inverse: crossRate(snapshot, to, from), result, parsed, missing: null }
    } catch (e) {
      if (e instanceof MissingRateError) {
        return { rate: null, inverse: null, result: null, parsed, missing: e.code }
      }
      throw e
    }
  }, [snapshot, from, to, amount])
}

export function ConverterCard({ className, showCommon = true }: { className?: string; showCommon?: boolean }) {
  const { t } = useTranslation()
  const fmt = useFormat()
  const online = useOnline()
  const { from, to, amount, setFrom, setTo, setAmount, swap, savedPairs, togglePair } = useConverter()
  const rates = useRates()
  const { snapshot } = rates
  const { rate, inverse, result, parsed, missing } = useConversion(snapshot)
  const [picker, setPicker] = useState<'from' | 'to' | null>(null)
  const [spin, setSpin] = useState(0)
  const [copied, setCopied] = useState(false)

  const freshness = snapshot
    ? getFreshness(snapshot.kind, snapshot.asOf, new Date(), maxAgeFor(snapshot.asOfPrecision))
    : null
  const loading = rates.status === 'loading'
  const deviation = snapshot?.checks?.deviations.find((d) => d.code === from || d.code === to)
  const showYer = !!snapshot && snapshot.kind !== 'demo' && (from === 'YER' || to === 'YER')
  const saved = isSavedPair(savedPairs, { from, to })
  const invalid = amount.trim() !== '' && !parsed

  const resultText = result ? fmt.fixed(result.display, result.fractionDigits) : '—'

  const copy = async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result.display.toFixed(result.fractionDigits))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard blocked: ignore */
    }
  }

  return (
    <section className={clsx('card relative p-4 sm:p-5', className)} aria-labelledby="converter-title">
      <header className="mb-4 flex items-center gap-2">
        <h2 id="converter-title" className="text-base font-semibold tracking-tight">
          {t('converter.title')}
        </h2>
        {snapshot && <KindBadge kind={snapshot.kind} />}
        <button
          type="button"
          onClick={() => togglePair({ from, to })}
          aria-pressed={saved}
          aria-label={saved ? t('converter.unpinPair') : t('converter.pinPair')}
          title={saved ? t('converter.unpinPair') : t('converter.pinPair')}
          className={clsx(
            'ms-auto inline-flex size-9 items-center justify-center rounded-xl transition hover:bg-surface-2',
            saved ? 'text-gold-400' : 'text-subtle',
          )}
        >
          <Star className="size-[1.1rem]" fill={saved ? 'currentColor' : 'none'} />
        </button>
      </header>

      <div className="relative flex flex-col gap-2">
        {/* FROM */}
        <div
          className={clsx(
            'rounded-2xl border bg-surface-2 px-3 pt-2.5 pb-3 transition focus-within:border-gold-400/50',
            invalid ? 'border-down/50' : 'border-line',
          )}
        >
          <label htmlFor="gx-amount" className="mb-1 block px-1 text-xs font-medium text-subtle">
            {t('converter.from')}
          </label>
          <div className="flex items-center gap-3">
            <CurrencyButton code={from} label={t('converter.from')} onClick={() => setPicker('from')} />
            <AmountInput
              id="gx-amount"
              aria-invalid={invalid}
              aria-describedby={invalid ? 'gx-amount-error' : undefined}
              value={amount}
              onValueChange={setAmount}
              placeholder="0"
              className="num min-w-0 flex-1 bg-transparent text-right text-[1.5rem] font-semibold tracking-tight xs:text-[1.75rem] outline-none placeholder:text-subtle sm:text-[2rem] rtl:text-left"
            />
          </div>
        </div>

        {/* SWAP */}
        <button
          type="button"
          onClick={() => {
            swap()
            setSpin((s) => s + 180)
          }}
          aria-label={t('converter.swap')}
          title={t('converter.swap')}
          className="absolute top-1/2 left-1/2 z-10 flex size-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gold-400/40 bg-surface-1 text-gold-text shadow-card transition hover:border-gold-400 hover:shadow-gold"
        >
          <ArrowDownUp
            className="size-[1.15rem] transition-transform duration-500 ease-out-soft"
            style={{ transform: `rotate(${spin}deg)` }}
          />
        </button>

        {/* TO */}
        <div className="rounded-2xl border border-line bg-surface-2 px-3 pt-2.5 pb-3">
          <span className="mb-1 block px-1 text-xs font-medium text-subtle">{t('converter.to')}</span>
          <div className="flex items-center gap-3">
            <CurrencyButton code={to} label={t('converter.to')} onClick={() => setPicker('to')} />
            <output
              htmlFor="gx-amount"
              aria-live="polite"
              className="num ltr min-w-0 flex-1 truncate text-right text-[1.5rem] font-semibold tracking-tight xs:text-[1.75rem] sm:text-[2rem] rtl:text-left"
            >
              {loading ? (
                <Skeleton className="ms-auto h-9 w-32 rtl:ms-0 rtl:me-auto" />
              ) : (
                <span key={resultText} className="inline-block animate-[gx-rise_220ms_ease-out]">
                  {resultText}
                </span>
              )}
            </output>
            <button
              type="button"
              onClick={copy}
              disabled={!result}
              aria-label={copied ? t('converter.copied') : t('converter.copy')}
              title={copied ? t('converter.copied') : t('converter.copy')}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl text-subtle transition hover:bg-surface-3 hover:text-fg"
            >
              {copied ? <Check className="size-4 text-up" /> : <Copy className="size-4" />}
            </button>
          </div>
        </div>
      </div>

      {invalid && (
        <p id="gx-amount-error" role="alert" className="mt-2 px-1 text-xs text-down">
          {t('converter.invalid')}
        </p>
      )}
      {missing && (
        <p role="alert" className="mt-2 px-1 text-xs text-down">
          {t('converter.unavailable', { code: missing })}
        </p>
      )}

      {/* RATE + META */}
      {rate && inverse && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 px-1">
          <p className="num ltr text-sm font-medium">
            1 {from} = {fmt.fixed(rate, rateFractionDigits(rate))} {to}
          </p>
          <p className="num ltr text-xs text-subtle">
            1 {to} = {fmt.fixed(inverse, rateFractionDigits(inverse))} {from}
          </p>
        </div>
      )}

      {rates.status === 'error' && (
        <div role="alert" className="mt-4 flex flex-wrap items-center gap-3 rounded-xl bg-down-soft px-3 py-2.5 text-xs text-down">
          <AlertTriangle className="size-4 shrink-0" aria-hidden />
          <span className="flex-1">{t('converter.loadError')}</span>
          <Button size="sm" variant="outline" onClick={rates.refetch} disabled={rates.isFetching}>
            <RefreshCw className={clsx('size-3.5', rates.isFetching && 'animate-spin')} aria-hidden />
            {t('converter.retry')}
          </Button>
        </div>
      )}
      {loading && <Skeleton className="mt-4 h-24 w-full rounded-xl" />}

      {snapshot && (
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-xl bg-surface-0/60 px-3 py-2.5 text-xs ring-1 ring-line">
        <dt className="text-subtle">{t('common.source')}</dt>
        <dd className="min-w-0 text-muted">
          {snapshot.source.url ? (
            <a
              href={snapshot.source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex max-w-full items-center gap-1 hover:text-fg hover:underline"
            >
              <span className="truncate">{snapshot.source.name}</span>
              <ExternalLink className="size-3 shrink-0" aria-hidden />
            </a>
          ) : (
            snapshot.source.name
          )}
        </dd>
        <dt className="text-subtle">{t('common.updated')}</dt>
        <dd className="text-muted">
          <time dateTime={snapshot.asOf}>
            {snapshot.asOfPrecision === 'day' ? fmt.date(snapshot.asOf) : fmt.dateTime(snapshot.asOf)}
          </time>
          {snapshot.asOfPrecision === 'day' && <span className="text-subtle"> · {t('converter.dailyRate')}</span>}
        </dd>
        <dt className="text-subtle">{t('common.type')}</dt>
        <dd className="text-muted">{t(`converter.kindHint.${snapshot.kind}`)}</dd>
        {result && (
          <>
            <dt className="text-subtle">{t('common.precision')}</dt>
            <dd className="text-muted">{t('converter.precision', { n: result.fractionDigits, code: to })}</dd>
          </>
        )}
      </dl>
      )}

      {showYer && (
        <p role="note" className="mt-3 flex gap-2 rounded-xl bg-warn-soft px-3 py-2 text-xs text-warn">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {t('converter.yerNotice')}
        </p>
      )}
      {deviation && (
        <p role="note" className="mt-3 rounded-xl bg-surface-2 px-3 py-2 text-xs text-muted ring-1 ring-line">
          {t('converter.deviation', { code: deviation.code, pct: fmt.fixed(deviation.diffPct, 1) })}
        </p>
      )}

      {snapshot && freshness === 'stale' && (
        <p role="status" className="mt-3 rounded-xl bg-warn-soft px-3 py-2 text-xs text-warn">
          {t('converter.stale', { time: fmt.relative(snapshot.asOf) })}
        </p>
      )}
      {(!online || (rates.fromDeviceCache && !rates.isFetching)) && snapshot && (
        <p role="status" className="mt-3 flex items-center gap-2 rounded-xl bg-warn-soft px-3 py-2 text-xs text-warn">
          <WifiOff className="size-3.5" aria-hidden />
          {online ? t('converter.cached') : t('converter.offline')}
        </p>
      )}

      {showCommon && (
        <div className="mt-4">
          <p className="mb-2 px-1 text-xs font-medium text-subtle">{t('converter.popular')}</p>
          <div className="flex flex-wrap gap-1.5">
            {COMMON_AMOUNTS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAmount(a)}
                aria-pressed={amount === a}
                className={clsx(
                  'num ltr h-8 rounded-lg px-3 text-xs font-semibold transition',
                  amount === a
                    ? 'bg-gold-400/15 text-gold-text ring-1 ring-gold-400/40'
                    : 'bg-surface-2 text-muted ring-1 ring-line hover:text-fg',
                )}
              >
                {fmt.fixed(a, 0)}
              </button>
            ))}
          </div>
        </div>
      )}

      <CurrencyPicker
        open={picker !== null}
        onOpenChange={(o) => !o && setPicker(null)}
        selected={picker === 'to' ? to : from}
        onSelect={(code) => (picker === 'to' ? setTo(code) : setFrom(code))}
      />
    </section>
  )
}
